/**
 * @file 鉴权相关 API
 * @description 登录等接口；页面通过本模块调用，不直接碰 axios。
 *
 * @whenToUse 登录页、刷新 token 等鉴权流程。
 * @whenNotToUse 业务资源 CRUD（放到对应业务 api 文件）。
 */

import { get, post } from '@/lib/client.ts'

/** 登录请求体（对齐 plus-ui LoginData，走密码授权） */
export type LoginParams = {
  /** 账号 */
  username: string
  /** 密码 */
  password: string
  /** 是否记住账号 */
  rememberMe: boolean
  /** 客户端授权 id（由接口层注入） */
  clientId: string
  /** 授权类型，固定 password */
  grantType: string
  /** 租户 id */
  tenantId: string
}

/** 登录成功数据 */
export type LoginResult = {
  /** 访问令牌，写入 AUTH_TOKEN */
  access_token: string
  /** 刷新令牌 */
  refresh_token: string
  /** access_token 有效期（秒） */
  expire_in: number
}

/** 当前用户基本信息（/system/user/getInfo 的 user 字段，按需取子集） */
export type UserInfoUser = {
  /** 用户账号 */
  userName: string
  /** 用户昵称 */
  nickName: string
  /** 充值点数（当前账号可用点数） */
  points: number
  /** 是否主账号：'1' 主账号，'0' 子账号 */
  isMain: '1' | '0'
}

/** 当前登录用户信息 */
export type UserInfo = {
  /** 用户基本信息 */
  user: UserInfoUser
  /** 角色权限标识 */
  roles: string[]
  /** 菜单权限标识 */
  permissions: string[]
}

/**
 * 账号密码登录。
 * @param params 账号密码
 * @returns 含 access_token 的登录结果
 *
 * @example
 * ```ts
 * const { access_token } = await loginApi({ username, password })
 * setAuthToken(access_token)
 * ```
 */
export function loginApi(params: Omit<LoginParams, 'clientId' | 'grantType'>) {
  return post<LoginResult>(
    '/auth/login',
    {
      ...params,
      clientId: import.meta.env.VITE_APP_CLIENT_ID,
      grantType: 'password',
    },
    // 仅登录请求体加密；其余接口一律明文
    { encrypt: true },
  )
}

/**
 * 退出登录（服务端吊销令牌）。
 * @returns 空数据；无论成败本地都应清除 token
 */
export function logoutApi() {
  return post<null>('/auth/logout')
}

/**
 * 获取当前登录用户信息（含角色 / 菜单权限）。
 * @returns 用户信息
 */
export function getUserInfoApi() {
  return get<UserInfo>('/system/user/getInfo')
}
